import { TravelAlertTypesID } from "@shared/constants/travel-alert-types.enum";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";

/**
 * Colors for the 7 alert types in the horizontal bar chart.
 * Order matches the seed in `tpe_travel_analysis_alert_types`.
 */
export const ALERT_TYPE_COLORS: string[] = [
  "#ff6c5c", // Ciclo transversal (D+1)
  "#ff9800", // Déficit de comandos de hardware
  "#ffc107", // Déficit de comandos de sensor (IDLE elevado)
  "#e91e63", // Falha de posicionamento GPS
  "#9c27b0", // Inconsistência de fluxo logístico
  "#3f51b5", // Informações de CAN travadas
  "#00bcd4", // Instabilidade de conexão GPRS
  "#f44336"  // Falsos positivos de descarga
];

/** A single alert type entry rendered in the help glossary drawer. */
export interface AlertGlossaryEntry {
  /** Alert type UUID (matches TravelAlertTypesID). */
  id: string;
  /** i18n key for the alert title. */
  titleKey: TravelAnalysisMessages;
  /** i18n key for the "what it detects" description. */
  descriptionKey: TravelAnalysisMessages;
  /** i18n key for the "when it fires" condition. */
  whenKey: TravelAnalysisMessages;
  /** i18n key for the affected vehicle scope. */
  appliesKey: TravelAnalysisMessages;
  /** i18n key for the hint on how to read the metadata. */
  metadataHintKey: TravelAnalysisMessages;
  /** Existing metadata label i18n keys captured by this alert, shown as chips. */
  metadataKeys: TravelAnalysisMessages[];
}

/**
 * Static description of every travel-analysis alert type, shown in the help
 * glossary regardless of which alerts appear in the current dashboard period.
 * Order: alerts that apply to all vehicles first, then mixer/pump-specific ones.
 */
export const ALERT_GLOSSARY: AlertGlossaryEntry[] = [
  {
    id: TravelAlertTypesID.INSTABILIDADE_GPRS,
    titleKey: TravelAnalysisMessages.glossaryGprsTitle,
    descriptionKey: TravelAnalysisMessages.glossaryGprsDesc,
    whenKey: TravelAnalysisMessages.glossaryGprsWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesAll,
    metadataHintKey: TravelAnalysisMessages.glossaryGprsMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaTotalPackets,
      TravelAnalysisMessages.metaRealtimeCommands,
      TravelAnalysisMessages.metaMemoryCommands,
      TravelAnalysisMessages.metaEffectiveRealtimeCommands,
      TravelAnalysisMessages.metaMemoryRatio,
      TravelAnalysisMessages.metaThresholdPercent,
      TravelAnalysisMessages.metaEffectiveRealtimeThresholdSeconds,
      TravelAnalysisMessages.metaAvgDelaySeconds,
      TravelAnalysisMessages.metaMaxDelaySeconds
    ]
  },
  {
    id: TravelAlertTypesID.FALHA_POSICIONAMENTO_GPS,
    titleKey: TravelAnalysisMessages.glossaryGpsTitle,
    descriptionKey: TravelAnalysisMessages.glossaryGpsDesc,
    whenKey: TravelAnalysisMessages.glossaryGpsWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesAll,
    metadataHintKey: TravelAnalysisMessages.glossaryGpsMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaTotalGpsPackets,
      TravelAnalysisMessages.metaInvalidFixCount,
      TravelAnalysisMessages.metaInvalidRatio,
      TravelAnalysisMessages.metaThresholdPercent,
      TravelAnalysisMessages.metaAvgSatellites
    ]
  },
  {
    id: TravelAlertTypesID.CICLO_TRANSVERSAL,
    titleKey: TravelAnalysisMessages.glossaryCrossDayTitle,
    descriptionKey: TravelAnalysisMessages.glossaryCrossDayDesc,
    whenKey: TravelAnalysisMessages.glossaryCrossDayWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixerPump,
    metadataHintKey: TravelAnalysisMessages.glossaryCrossDayMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaStartDate,
      TravelAnalysisMessages.metaFinishDate,
      TravelAnalysisMessages.metaStartDay,
      TravelAnalysisMessages.metaFinishDay,
      TravelAnalysisMessages.metaDurationHours
    ]
  },
  {
    id: TravelAlertTypesID.DEFICIT_COMANDOS_HARDWARE,
    titleKey: TravelAnalysisMessages.glossaryHwDeficitTitle,
    descriptionKey: TravelAnalysisMessages.glossaryHwDeficitDesc,
    whenKey: TravelAnalysisMessages.glossaryHwDeficitWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixerPump,
    metadataHintKey: TravelAnalysisMessages.glossaryHwDeficitMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaPacketCount,
      TravelAnalysisMessages.metaExpectedPackets,
      TravelAnalysisMessages.metaMinimumRequired,
      TravelAnalysisMessages.metaDeficitPercent,
      TravelAnalysisMessages.metaRatePerMinute,
      TravelAnalysisMessages.metaTravelDurationMinutes,
      TravelAnalysisMessages.metaThresholdPercent
    ]
  },
  {
    id: TravelAlertTypesID.DEFICIT_COMANDOS_SENSOR,
    titleKey: TravelAnalysisMessages.glossarySensorIdleTitle,
    descriptionKey: TravelAnalysisMessages.glossarySensorIdleDesc,
    whenKey: TravelAnalysisMessages.glossarySensorIdleWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixer,
    metadataHintKey: TravelAnalysisMessages.glossarySensorIdleMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaTotalPackets,
      TravelAnalysisMessages.metaIgnitionOnCount,
      TravelAnalysisMessages.metaIdleCount,
      TravelAnalysisMessages.metaLoadingCount,
      TravelAnalysisMessages.metaUnloadingCount,
      TravelAnalysisMessages.metaIdleRatio,
      TravelAnalysisMessages.metaThresholdPercent
    ]
  },
  {
    id: TravelAlertTypesID.INCONSISTENCIA_FLUXO_LOGISTICO,
    titleKey: TravelAnalysisMessages.glossaryLogisticsTitle,
    descriptionKey: TravelAnalysisMessages.glossaryLogisticsDesc,
    whenKey: TravelAnalysisMessages.glossaryLogisticsWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixerDoc,
    metadataHintKey: TravelAnalysisMessages.glossaryLogisticsMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaRequiredStates,
      TravelAnalysisMessages.metaActualStates,
      TravelAnalysisMessages.metaMissingStates,
      TravelAnalysisMessages.metaMissingCount,
      TravelAnalysisMessages.metaTotalStates,
      TravelAnalysisMessages.metaNumDoc
    ]
  },
  {
    id: TravelAlertTypesID.CAN_TRAVADO,
    titleKey: TravelAnalysisMessages.glossaryCanStallTitle,
    descriptionKey: TravelAnalysisMessages.glossaryCanStallDesc,
    whenKey: TravelAnalysisMessages.glossaryCanStallWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixer,
    metadataHintKey: TravelAnalysisMessages.glossaryCanStallMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaSource,
      TravelAnalysisMessages.metaStallType,
      TravelAnalysisMessages.metaThreshold,
      TravelAnalysisMessages.metaStalledFields,
      TravelAnalysisMessages.metaTotalCanPackets
    ]
  },
  {
    id: TravelAlertTypesID.FALSO_POSITIVO_DESCARGA,
    titleKey: TravelAnalysisMessages.glossaryFalseDischargeTitle,
    descriptionKey: TravelAnalysisMessages.glossaryFalseDischargeDesc,
    whenKey: TravelAnalysisMessages.glossaryFalseDischargeWhen,
    appliesKey: TravelAnalysisMessages.glossaryAppliesMixer,
    metadataHintKey: TravelAnalysisMessages.glossaryFalseDischargeMeta,
    metadataKeys: [
      TravelAnalysisMessages.metaTotalDischargeCommands,
      TravelAnalysisMessages.metaCoveredCommands,
      TravelAnalysisMessages.metaOrphanCommands,
      TravelAnalysisMessages.metaOrphanRatio,
      TravelAnalysisMessages.metaCoveredRanges,
      TravelAnalysisMessages.metaThreshold
    ]
  }
];

/** Colors for success vs alerts segments in the doughnut chart. */
export const SUCCESS_RATIO_COLORS = {
  success: "#4caf50",
  alerts: "#ff6c5c"
};

/** Default filter period in days when no date range is selected. */
export const DEFAULT_PERIOD_DAYS = 30;

/** Target success rate percentage shown on the KPI card. */
export const SUCCESS_RATE_TARGET = 90;
